import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import Drawer from '../components/Drawer';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { money, date } from '../lib/format';

const today = () => new Date().toISOString().slice(0, 10);
const METHOD = { check: 'Check', ach: 'ACH', wire: 'Wire', cash: 'Cash', stripe: 'Stripe', other: 'Other' };
const r2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

export default function Payments() {
  const { activeId, activeBusiness } = useBusiness();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Collect-payment drawer
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [depositAccounts, setDepositAccounts] = useState([]);
  const [form, setForm] = useState(null);
  const [openInvoices, setOpenInvoices] = useState([]);
  const [loadingInv, setLoadingInv] = useState(false);
  const [alloc, setAlloc] = useState({}); // invoice_id -> amount string
  const [distribute, setDistribute] = useState('');
  const [custRetainer, setCustRetainer] = useState(null); // { available } for chosen customer
  const [applyingCredit, setApplyingCredit] = useState(false);
  const [creditNote, setCreditNote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    api(`/payments?business_id=${activeId}`)
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch(() => setError('Could not load payments.'))
      .finally(() => setLoading(false));
  }, [activeId]);
  useEffect(() => { load(); }, [load]);

  // Reference data for the drawer
  useEffect(() => {
    api(`/customers?business_id=${activeId}`)
      .then((d) => setCustomers((Array.isArray(d) ? d : []).filter((c) => c.is_active !== false)))
      .catch(() => setCustomers([]));
    api(`/accounts?business_id=${activeId}&type=asset`)
      .then((d) => setDepositAccounts((Array.isArray(d) ? d : []).filter((a) => a.is_bank || a.code === '1010')))
      .catch(() => setDepositAccounts([]));
  }, [activeId]);

  const total = useMemo(() => rows.reduce((s, r) => s + Number(r.amount || 0), 0), [rows]);

  function openCollect() {
    const def = depositAccounts.find((a) => a.code === '1010') || depositAccounts[0];
    setForm({ customer_id: '', payment_date: today(), method: 'check', reference: '', deposit_account_id: def ? def.id : '', notes: '' });
    setOpenInvoices([]); setAlloc({}); setDistribute(''); setFormError(null); setCustRetainer(null); setCreditNote(null); setOpen(true);
  }
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Load the chosen customer's outstanding invoices (oldest first) + their retainer credit.
  const loadCustomer = useCallback((cid) => {
    if (!cid) { setOpenInvoices([]); setAlloc({}); setCustRetainer(null); return; }
    setLoadingInv(true);
    api(`/invoices?business_id=${activeId}&customer_id=${cid}&outstanding=1`)
      .then((d) => {
        const list = (Array.isArray(d) ? d : []).sort((a, b) =>
          String(a.due_date || a.issue_date).localeCompare(String(b.due_date || b.issue_date)));
        setOpenInvoices(list); setAlloc({}); setDistribute('');
      })
      .catch(() => setOpenInvoices([]))
      .finally(() => setLoadingInv(false));
    api(`/invoices/retainer-balance?business_id=${activeId}&customer_id=${cid}`)
      .then((d) => setCustRetainer(d)).catch(() => setCustRetainer(null));
  }, [activeId]);

  useEffect(() => {
    if (!open) return;
    loadCustomer(form?.customer_id);
  }, [open, form?.customer_id, loadCustomer]);

  // Apply available retainer credit to this customer's open invoices, oldest first.
  async function applyRetainerCredit() {
    const cid = form?.customer_id;
    if (!cid || !custRetainer || custRetainer.available <= 0) return;
    setApplyingCredit(true); setCreditNote(null); setFormError(null);
    let remaining = custRetainer.available;
    let appliedTotal = 0;
    try {
      for (const inv of openInvoices) {
        if (remaining <= 0.005) break;
        const amt = r2(Math.min(remaining, Number(inv.balance)));
        if (amt <= 0) continue;
        const res = await api(`/invoices/${inv.id}/apply-retainer`, { method: 'POST', body: { amount: amt } });
        appliedTotal = r2(appliedTotal + (res.applied || amt));
        remaining = r2(remaining - (res.applied || amt));
      }
      setCreditNote({ ok: true, text: `Applied ${money(appliedTotal)} of retainer credit.` });
      loadCustomer(cid);
    } catch (e) {
      setCreditNote({ ok: false, text: e?.detail || 'Could not apply retainer credit.' });
      loadCustomer(cid);
    } finally { setApplyingCredit(false); }
  }


  const setAllocFor = (id, v) => setAlloc((a) => ({ ...a, [id]: v }));
  const allocated = useMemo(
    () => r2(openInvoices.reduce((s, i) => s + (Number(alloc[i.id]) || 0), 0)),
    [alloc, openInvoices]
  );

  function autoAllocate() {
    let remaining = Number(distribute) || 0;
    if (!(remaining > 0)) { setFormError('Enter an amount to apply first.'); return; }
    setFormError(null);
    const next = {};
    for (const inv of openInvoices) {
      if (remaining <= 0) break;
      const take = Math.min(remaining, Number(inv.balance));
      if (take > 0) { next[inv.id] = String(r2(take)); remaining = r2(remaining - take); }
    }
    setAlloc(next);
  }

  async function submit() {
    if (!form.customer_id) { setFormError('Choose a customer.'); return; }
    const allocations = openInvoices
      .map((i) => ({ invoice_id: i.id, amount: Number(alloc[i.id]) || 0 }))
      .filter((a) => a.amount > 0);
    if (allocations.length === 0) { setFormError('Apply an amount to at least one invoice.'); return; }
    for (const i of openInvoices) {
      const amt = Number(alloc[i.id]) || 0;
      if (amt > Number(i.balance) + 0.001) { setFormError(`Amount on ${i.invoice_number} exceeds its balance.`); return; }
    }
    setSaving(true); setFormError(null);
    try {
      await api('/payments', {
        method: 'POST',
        body: {
          business_id: activeId,
          customer_id: Number(form.customer_id),
          payment_date: form.payment_date,
          method: form.method,
          reference: form.reference || null,
          deposit_account_id: form.deposit_account_id || null,
          notes: form.notes || null,
          allocations,
        },
      });
      setOpen(false);
      load();
    } catch (e) {
      setFormError(e?.detail || 'Could not record the payment.');
    } finally { setSaving(false); }
  }

  const columns = [
    { key: 'payment_date', header: 'Date', render: (r) => date(r.payment_date) },
    { key: 'customer', header: 'Customer', render: (r) => r.customer_name || '—' },
    { key: 'method', header: 'Method', render: (r) => METHOD[r.method] || r.method },
    { key: 'reference', header: 'Reference', render: (r) => r.reference || '—' },
    { key: 'applied_to', header: 'Applied to', render: (r) => r.applied_to || '—' },
    { key: 'amount', header: 'Amount', render: (r) => <span className="num">{money(r.amount)}</span> },
  ];

  const footer = (
    <>
      <button className="btn" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
      <button className="btn btn--primary" onClick={submit} disabled={saving || allocated <= 0}>
        {saving ? 'Recording…' : `Collect ${money(allocated)}`}
      </button>
    </>
  );

  return (
    <>
      <PageHeader
        title="Payments"
        subtitle={activeBusiness?.name}
        actions={can('payments.record') && <button className="btn btn--primary" onClick={openCollect}>Collect payment</button>}
      />
      <section className="panel">
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          error={error}
          getRowKey={(r) => r.id}
          empty="No payments recorded yet. Use Collect payment to record one."
        />
        {rows.length > 0 && (
          <div className="pay-total">
            <span>{rows.length} payment{rows.length === 1 ? '' : 's'}</span>
            <span className="num">{money(total)}</span>
          </div>
        )}
      </section>

      <Drawer open={open} title="Collect payment" width={720} onClose={() => !saving && setOpen(false)} footer={form && footer}>
        {form && (
          <>
            {formError && <div className="form-error">{formError}</div>}

            <div className="field">
              <label>Customer *</label>
              <select value={form.customer_id} onChange={(e) => setField('customer_id', e.target.value)}>
                <option value="">Select a customer…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ''}</option>)}
              </select>
            </div>

            <div className="form-grid">
              <div className="field"><label>Date</label>
                <input type="date" value={form.payment_date} onChange={(e) => setField('payment_date', e.target.value)} /></div>
              <div className="field"><label>Method</label>
                <select value={form.method} onChange={(e) => setField('method', e.target.value)}>
                  {Object.entries(METHOD).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select></div>
              <div className="field"><label>Reference</label>
                <input value={form.reference} placeholder="Check #, confirmation…" onChange={(e) => setField('reference', e.target.value)} /></div>
              <div className="field"><label>Deposit to</label>
                <select value={form.deposit_account_id} onChange={(e) => setField('deposit_account_id', e.target.value)}>
                  {depositAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
                </select></div>
            </div>

            {form.customer_id && (
              <>
                {custRetainer && custRetainer.available > 0 && (
                  <div className="pay-retainer" style={{ marginTop: 16 }}>
                    <div className="pay-retainer__head">
                      <span>Retainer / deposit credit available</span><span className="num">{money(custRetainer.available)}</span>
                    </div>
                    <div className="pay-retainer__row">
                      <button type="button" className="btn btn--sm" onClick={applyRetainerCredit} disabled={applyingCredit || openInvoices.length === 0}>
                        {applyingCredit ? 'Applying…' : 'Apply credit to open invoices (oldest first)'}
                      </button>
                    </div>
                    <span className="hint">Draws the customer’s deposit down against their open invoices before any cash payment.</span>
                    {creditNote && <div className={creditNote.ok ? 'form-note' : 'form-error'} style={{ marginTop: 8 }}>{creditNote.text}</div>}
                  </div>
                )}
                <h3 className="form-section">Outstanding invoices</h3>
                {loadingInv ? (
                  <p className="hint">Loading…</p>
                ) : openInvoices.length === 0 ? (
                  <p className="hint">This customer has no open invoices.</p>
                ) : (
                  <>
                    <div className="pay-apply">
                      <input type="number" step="0.01" value={distribute} placeholder="Amount to apply"
                        onChange={(e) => setDistribute(e.target.value)} />
                      <button type="button" className="btn btn--sm" onClick={autoAllocate}>Auto-allocate (oldest first)</button>
                    </div>
                    <table className="rep-table">
                      <thead><tr><th>Invoice</th><th>Due</th><th className="num">Balance</th><th className="num">Apply</th></tr></thead>
                      <tbody>
                        {openInvoices.map((i) => (
                          <tr key={i.id}>
                            <td>{i.invoice_number}{i.kind === 'retainer' ? ' (retainer)' : ''}</td>
                            <td>{date(i.due_date)}</td>
                            <td className="num">{money(i.balance)}</td>
                            <td className="num">
                              <input type="number" step="0.01" className="pay-apply__amt"
                                value={alloc[i.id] || ''} placeholder="0.00"
                                onChange={(e) => setAllocFor(i.id, e.target.value)} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot><tr><td colSpan={3}>Total to apply</td><td className="num">{money(allocated)}</td></tr></tfoot>
                    </table>
                    <div className="field" style={{ marginTop: 14 }}>
                      <label>Notes</label>
                      <input value={form.notes} onChange={(e) => setField('notes', e.target.value)} />
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}
